import { CommonModule, Location } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzAffixModule } from 'ng-zorro-antd/affix';
import { NzBreadCrumbModule } from 'ng-zorro-antd/breadcrumb';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

import { TenantPointGrantDrawerComponent, TenantPointGrantTenant } from './tenant-point-grant-drawer.component';

@Component({
  selector: 'app-tenant-list',
  templateUrl: './tenant-list.component.html',
  styleUrls: ['./tenant-list.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NzTableModule,
    NzSpinModule,
    NzBreadCrumbModule,
    NzPaginationModule,
    ReactiveFormsModule,
    NzFormModule,
    FormsModule,
    NzSelectModule,
    NzModalModule,
    NzMenuModule,
    NzInputModule,
    NzButtonModule,
    NzIconModule,
    NzToolTipModule,
    NzEmptyModule,
    NzTagModule,
    NzDropDownModule,
    NzDrawerModule,
    NzAffixModule,
    NzCardModule,
    I18nPipe,
    TestIdDirective,
    TenantPointGrantDrawerComponent
  ]
})
export class TenantListComponent implements OnInit {
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  private readonly http = inject(HttpService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);

  private readonly nzModalService = inject(NzModalService);
  tenantTypeMap: any = {
    Common: 'tenant.list.tenant-types.Common',
    Person: 'tenant.list.tenant-types.Person',
    Official: 'tenant.list.tenant-types.Official'
  };

  // 租户类型颜色映射
  tenantTypeColorMap: any = {
    Common: 'blue',
    Person: 'green',
    Official: 'red'
  };

  tenantStatusMap: Record<string, string> = {
    Enable: 'tenant.list.status.Enable',
    Audit: 'tenant.list.status.Audit',
    Disable: 'tenant.list.status.Disable'
  };

  tenantStatusColorMap: Record<string, string> = {
    Enable: 'green',
    Audit: 'blue',
    Disable: 'red'
  };

  // 类型筛选
  typeFilter: string | null = null;
  statusFilter: string | null = null;
  typeOptions = [
    { text: this.i18n.fanyi('tenant.list.tenant-types.Common'), value: 'Common' },
    { text: this.i18n.fanyi('tenant.list.tenant-types.Person'), value: 'Person' },
    { text: this.i18n.fanyi('tenant.list.tenant-types.Official'), value: 'Official' }
  ];
  statusOptions = [
    { text: this.i18n.fanyi('tenant.list.status.Enable'), value: 'Enable' },
    { text: this.i18n.fanyi('tenant.list.status.Audit'), value: 'Audit' },
    { text: this.i18n.fanyi('tenant.list.status.Disable'), value: 'Disable' }
  ];

  direction = 'desc';
  refreshTime = Date.now();
  loading = false;

  listOfData: any[] = [];

  columns = [
    { title: this.i18n.fanyi('tenant.tenantName'), key: 'name' },
    { title: this.i18n.fanyi('tenant.shortName'), key: 'shortName' },
    { title: this.i18n.fanyi('tenant.tenantCode'), key: 'code' },
    {
      title: this.i18n.fanyi('tenant.type'),
      key: 'type',
      filterMultiple: false,
      filterOptions: this.typeOptions
    },
    {
      title: this.i18n.fanyi('common.status'),
      key: 'status',
      filterMultiple: false,
      filterOptions: this.statusOptions
    },
    { title: this.i18n.fanyi('tenant.accountId'), key: 'accountId' }
  ];
  pageIndex = 1;
  pageSize = 20;
  total = 0;
  searchKeywords = '';
  pointGrantVisible = false;
  pointGrantTenant: TenantPointGrantTenant | null = null;

  search(): void {
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.loadData();
  }

  resetSearch(): void {
    this.searchKeywords = '';
    this.refreshTime = Date.now();
    this.pageIndex = 1;
    this.loadData();
  }

  nzPageIndexChange(index: number): void {
    this.pageIndex = index;
    this.loadData();
  }

  nzPageSizeChange(size: number): void {
    this.pageSize = size;
    this.refreshTime = Date.now();
    this.pageIndex = 1;
    this.loadData();
  }

  constructor() {}
  ngOnInit(): void {
    this.loadData();
  }
  loadData(): void {
    this.loading = true;
    this.http
      .post(ConstantUtil.ADMIN_TENANT_LIST, {
        pageIndex: this.pageIndex - 1,
        pageSize: this.pageSize,
        direction: this.direction,
        refreshTime: this.refreshTime,
        key: this.searchKeywords,
        type: this.typeFilter,
        status: this.statusFilter
      })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res.success) {
          this.listOfData = res.data.items;
          this.total = res.data.totalCount;
        }
      });
  }
  onFilterChange(key: string, value: string | null): void {
    if (key === 'type') {
      this.typeFilter = value;
    } else if (key === 'status') {
      this.statusFilter = value;
    }
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.loadData();
  }
  clearFilter(key: string): void {
    if (key === 'type') {
      this.typeFilter = null;
    } else if (key === 'status') {
      this.statusFilter = null;
    } else if (key === 'keywords') {
      this.searchKeywords = '';
    }
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.loadData();
  }
  // 获取租户类型标签
  getTenantTypeLabel(type: string): string {
    const i18nKey = this.tenantTypeMap[type];
    return i18nKey ? this.i18n.fanyi(i18nKey) : type;
  }

  // 获取租户类型颜色
  getTenantTypeColor(type: string): string {
    return this.tenantTypeColorMap[type] ?? 'default';
  }

  getTenantStatusLabel(status: string): string {
    const i18nKey = this.tenantStatusMap[status];
    return i18nKey ? this.i18n.fanyi(i18nKey) : status;
  }

  getTenantStatusColor(status: string): string {
    return this.tenantStatusColorMap[status] ?? 'default';
  }

  // 查看租户详情
  editTenant(tenant: any): void {
    this.router.navigate(['/tenants/detail', tenant.id]);
  }

  // 查看服务号列表
  viewServiceNumbers(tenant: any): void {
    this.router.navigate(['/tenants/detail', tenant.id], {
      queryParams: {
        tab: 'servicenumbers'
      }
    });
  }

  canGrantPoints(tenant: TenantPointGrantTenant | null | undefined): boolean {
    return String(tenant?.type ?? '').trim() === 'Person';
  }

  openPointGrant(tenant: TenantPointGrantTenant): void {
    this.pointGrantTenant = tenant;
    this.pointGrantVisible = true;
  }

  closePointGrant(): void {
    this.pointGrantVisible = false;
    this.pointGrantTenant = null;
  }
}
